import React, { useRef } from 'react';
import { Controller, useFormState } from "react-hook-form";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { downloadFile } from 'protocol-setup/redux/makeFileUploadRequest';

import { faPaperclip, faUpload } from '@fortawesome/pro-solid-svg-icons';
import { documentByIdUrl } from 'protocol-setup/redux/constants';
import { titleAndAria } from "protocol-setup/constants/errorMessages";
import FormErrorMessage from './FormErrorMessage';
import { OpenErrorNotification } from '../notification';

export function Pills({ children, block, className }) {
  return (
    <div
      data-testid="pill"
      className={`m-1 py-1 px-2 bg-cool-grey d-inline-block ${block ? "rounded" : "round"} ${className || ""}`}
      style={{ whiteSpace: "normal", minWidth: block && "15rem" }}
    >
      {children}
    </div>
  );
}

export function downloadDocument(row) {
  const documentUrl = documentByIdUrl(row.documentId);
    downloadFile(documentUrl)
    .then((response) => {
      const { blobResponse } = response;

      if (!blobResponse || !(blobResponse instanceof Blob)) {
        throw new Error('Invalid blob response received');
      }

      if (navigator.msSaveOrOpenBlob) {
        navigator.msSaveOrOpenBlob(blobResponse, `${row.documentFileName}`);
      } else {
        const url = window.URL.createObjectURL(blobResponse);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${row.documentFileName}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    }).catch(error => {
      OpenErrorNotification({ message: error.message || 'Error downloading document', autoHide: true });
    });
}

const FormFileUpload = ({
  name,
  label,
  hideLabel = false,
  labelClassName = '',
  wrapperClassName = '',
  control,
  disabled = false,
  required = false,
  multiple = false,
  trigger
}) => {
  const { errors } = useFormState({ control });
  const fileInputRef = useRef(null);

  const handleDocumentClick = () => {
    fileInputRef.current.click();
  };

  // Function to generate unique file name
  const generateUniqueFileName = (file, existingFiles) => {
    const existingNames = existingFiles.map(f => f.name);

    if (!existingNames.includes(file.name)) {
      return file.name;
    }

    // Extract file name and extension
    const lastDotIndex = file.name.lastIndexOf('.');
    const baseName = lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name;
    const extension = lastDotIndex !== -1 ? file.name.substring(lastDotIndex) : '';

    // Find the next available number
    let counter = 1;
    let newName;
    do {
      newName = `${baseName}_${counter}${extension}`;
      counter++;
    } while (existingNames.includes(newName));

    return newName;
  };

  // Function to create a new file object with renamed file
  const createRenamedFile = (originalFile, newName) => {
    const renamedFile = new File([originalFile], newName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified
    });
    return renamedFile;
  };

  return (
    <div className={`form-field ${wrapperClassName}`}>
      <label htmlFor={name} className={`form-label ${hideLabel ? "sr-only" : ""} ${labelClassName}`}>
        {required && (
          <span className="form-layout-required" aria-label="required">
            *
          </span>
        )}
        {label}
        <span className="text-xs text-gray-400 font-italic ml-1">(attachments should be redacted, remove PII)</span>
      </label>
      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        render={({ field: { onChange, value } }) => (
          <div className="flex flex-col my-2">
            <div className="flex flex-wrap my-3">
              {value && value.length > 0 && value.map((file) => (
                <Pills block={false} key={file.name}>
                  <span>
                    <button
                      type="button"
                      data-fileid={JSON.stringify(file)}
                      className="fn-btn icon-only mr-1 color-dark-blue"
                      // disabled={disabled}
                      onClick={() => downloadDocument({ documentId: file.id, documentFileName: file.name })}
                      aria-hidden="true">
                      <FontAwesomeIcon icon={faPaperclip} />
                      <span className="text-sm p-1">{file.name}</span>
                    </button>
                    {!disabled && (
                      <button type="button" className="fn-btn delete" disabled={disabled}>
                        <svg
                          aria-hidden="true"
                          focusable="false"
                          data-prefix="fas"
                          data-icon="trash-alt"
                          role="img"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 448 512"
                          className="svg-inline--fa fa-trash-alt"
                          onClick={() => {
                            const updatedFiles = value.filter((item) => item.name !== file.name);
                            onChange(updatedFiles);
                          }}
                        >
                          <path
                            fill="currentColor"
                            d="M32 464a48 48 0 0 0 48 48h288a48 48 0 0 0 48-48V128H32zm272-256a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zM432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16z"
                            className=""
                          />
                        </svg>
                      </button>
                    )}
                  </span>
                </Pills>
              ))}
            </div>
            <input
              id={name}
              type="file"
              ref={fileInputRef}
              className="invisible"
              multiple={multiple}
              disabled={disabled}
              onChange={(e) => {
                if (multiple) {
                  const files = Array.from(e.target.files);
                  const processedFiles = files.map(file => {
                    const uniqueName = generateUniqueFileName(file, value);
                    return uniqueName === file.name ? file : createRenamedFile(file, uniqueName);
                  });
                  onChange([...value, ...processedFiles]);
                } else {
                  const file = e.target.files[0];
                  const uniqueName = generateUniqueFileName(file, value);
                  const processedFile = uniqueName === file.name ? file : createRenamedFile(file, uniqueName);
                  onChange([processedFile]);
                }
                e.target.value = '';
                if (trigger) {
                  trigger(name);
                }
              }}
            />
            {!disabled && ( 
              <button
                type="button"
                onClick={handleDocumentClick}
                disabled={disabled}
                className="btn turquoise mr-1"
                aria-label={titleAndAria.attachmentRedactionWarning(label)}
                title={titleAndAria.attachmentRedactionWarning(label)}
              >
                <FontAwesomeIcon icon={faUpload} className="ml-n1" />
                Document
              </button>
            )}
          </div>
        )}
      />
      {errors[name] && (
        <FormErrorMessage message={errors[name].message} />
      )}
    </div>
  );
};

export default FormFileUpload;
